/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 30/9/2020
 */
import BaseObject from "@shared/BaseObject.js";
import CommonObject from "@shared/CommonObject";
import AuthManager from "@/core/js/backend/AuthManager";
import {RoleEnum} from "@/core/js/model/Permissions/RoleEnum.js";
import {RedirectCookie} from "@/core/js/Cookie/RedirectCookie";
import {AccountTypeEnum} from "@shared/Subscription/AccountTypeEnum.js";
import UserSubscription from "@shared/Subscription/UserSubscription.js";
import {plainToClass} from "@qwoach/class-transformer-wrapper";
import {isAppTheraLogik} from "@/utils/helpers";

import {AuthStateEnum} from "@shared/AuthStateEnum.js";
import {getRequiredRole, isWhitelisted} from "@/router/utils/routeAccess.js";

export default class RouterManager extends BaseObject {

	authManager: AuthManager
	router

	constructor(router, authManager, store, options = {}) {
		super();
		this.router = router;
		this.store = store;
		this.authManager = authManager;
		this.redirectCookie = new RedirectCookie()
		this.options = options;
		this.isInitialAppEntryTracked = false

		this.setup()
	}

	setup(){
		this.router.beforeEach(async (to, from) => {
			//we need to wait for the token to be set before we can proceed
			// because redirectGuard relies on login state which is set after the token is set
			this.authManager.logAuthTrace?.('router.guard_start', {to: to.name, from: from.name})
			await this.waitForTokenSet()
			this.authManager.logAuthTrace?.('router.guard_ready', {to: to.name, from: from.name})

			if(this.store?.state.store.authState === AuthStateEnum.ERROR) return true
			return this.generalRedirectGuard(to, from)
		})
		this.router.afterEach((to) => {
			this.trackAppEntryIfNeeded(to)
		})
	}

	async trackAppEntryIfNeeded(to){
		if(this.isInitialAppEntryTracked){
			return
		}

		this.isInitialAppEntryTracked = true
		let options = {pageViewProps: {title: to?.name || ""}}
		await CommonObject.analyticManager.trackAppEntry(options)
	}



	async loginSaveOrRestoreRoute(to, from){
		let isToLogin = (to.name || "").indexOf("Login") >= 0
		let isFromLogin = (from.name || "").indexOf("Login") >= 0
		let pathFrom = (from.path || "/")
		console.log("RouterManager loginSaveOrRestoreRoute: isToLogin = ", isToLogin, ", isFromLogin = ", isFromLogin, ", pathFrom = ", pathFrom)
		if(isToLogin && isFromLogin || !isToLogin && !isFromLogin || pathFrom === "/"){
			return false
		}
		// if(isToLogin){
		// 	console.log("RouterManager loginSaveOrRestoreRoute: isToLogin = true")
		// 	// this.redirectCookie.saveRedirectPath(from.path)
		// 	return false
		// }
		if(isFromLogin){
			let redirectUrl = this.redirectCookie.getAndClear()
			console.log("RouterManager loginSaveOrRestoreRoute: isFromLogin = true, redirectUrl = ", redirectUrl)
			if(redirectUrl){
				return redirectUrl
			}
			return false
		}
	}



	redirect(path, to, from){
		if (to.path !== path) {
			return {path}
		}
		return false
	}


	redirectExpired(to, from){
		let userProfile = this.authManager.getUserProfile()
		let userRole = userProfile ? userProfile.currentPortalRef.role : null;
		let userSubscription = this.getUserSubscription()
		let allowExpiredSubscription = to.matched.some(route => route.meta.allowExpiredSubscription)
		if(userRole === RoleEnum.COACH && (!userSubscription || !userSubscription.isAccessValid())) {
			console.log("RouterManager generalRedirectGuard: account expired: from.path = ", from.path)
			if(allowExpiredSubscription){
				return false
			}
			let name = "ProfileSubscriptionRoute"
			// if(this.isQwoachV2App()){
			// 	name = "RecoveryProfileSubscriptionRoute"
			// }
			return {name}
		}
	}

	isQwoachV2App(){
		console.log("RouterManager.isQwoachV2App: isAppTheraLogik = ", isAppTheraLogik)
		if(isAppTheraLogik){
			return false;
		}
		return this.authManager.isQwoachV2App()
	}

	async generalRedirectGuard(to, from){
		console.log("RouterManager generalRedirectGuard: to = ", to, ", from = ", from)
		let routeNameRedirect = this.redirectRouteNameIfNeeded(to)
		if (routeNameRedirect) {
			return routeNameRedirect
		}

		let isQwoachV2 = this.isQwoachV2App()
		let userRole = this.authManager.getUserRole();
		let isLoggedIn = this.authManager.isLoggedIn()
		let isWhitelisted = this.isWhitelistedFn(to, from)
		let requiredRole = this.getRequiredRole(to)

		let userSubscription = this.getUserSubscription()

		console.log("RouterManager.generalRedirectGuard: userRole = ", userRole, ", isLoggedIn = ", isLoggedIn, ", isWhitelisted = ", isWhitelisted, ", requiredRole = ", requiredRole, ", isQwoachV2 = ", isQwoachV2)

		// if(!this.isInvitedClient()){
		// 	return await this.logout()
		// }

		if(userSubscription && userSubscription.accountType === AccountTypeEnum.GENOGRAM
			&& to.name === "DashboardListRoute"){
			return this.redirectToClients(to, from)
		}

		let res = this.shouldRedirectToRecovery(to, from)
		if(res){
			return res
		}

		console.log("RouterManager generalRedirectGuard: requiredRole = ", requiredRole, ", userRole = ", userRole, ", isLoggedIn = ", isLoggedIn, ", isWhitelisted = ", isWhitelisted, ", isQwoachV2 = ",isQwoachV2)

		if(isWhitelisted && !requiredRole){
			console.log("RouterManager public route - proceed")
			return true
		}

		if(!isLoggedIn){
			console.log("RouterManager generalRedirectGuard: not logged in - redirecting to login...")
			return this.redirectToLogin()

		}

		res = this.redirectForRequiredRole(requiredRole, userRole, isQwoachV2)
		if(res){
			return res
		}

		res = this.redirectExpired(to, from)
		console.log("RouterManager generalRedirectGuard: redirectExpired = ", res)
		if(res){
			return res
		}





		let newPath = this.syncPathWithRole(to, userRole, isQwoachV2)
		if(newPath){
			console.log("RouterManager generalRedirectGuard: path synced with role: newPath = ", newPath)
			return newPath
		}


		return true
	}

	redirectForRequiredRole(requiredRole, userRole, isQwoachV2){
		if(!requiredRole || userRole === requiredRole){
			return false
		}
		if(userRole === RoleEnum.COACH) {
			return {name: 'CoachMain'}
		}
		if(userRole === RoleEnum.CLIENT) {
			return isQwoachV2 ? {name: 'ClientMain'} : {name: 'ClientPortalOldRouteName'}
		}
		return this.redirectToLogin()
	}

	// isInvitedClient(){
	// 	if(this.authManager.isClient()){
	// 		let isQwoachV2 = this.isQwoachV2App()
	// 		let userProfile = this.authManager.getUserProfile()
	// 		if(isQwoachV2 && !userProfile.portalRef){
	// 			return false
	// 		}
	// 	}
	// 	return true
	//
	// }
	redirectToClients(to, from){
		return {name: "ClientListMainRoute"}
	}

	shouldRedirectToRecovery(to, from){
		let isQwoachV2 = this.isQwoachV2App()
		if(isQwoachV2 && to.path.indexOf("app/coaching") >= 0){
			console.log("RouterManager RouterManager redirect to recovery")
			return {name: 'CoachMain'}//'/recovery'
		}
		if(isQwoachV2 && to.path.indexOf("cl/coaching") >= 0){
			console.log("RouterManager redirect to portal")
			return {name: 'ClientMain'}//'/portal'
		}
		return false

	}


	getUserSubscription(){
		if(this.store){
			let store = this.store.state.store
			return plainToClass(UserSubscription, store.ownerSubscription || store.userSubscription);
		}
		return null
	}

	async waitForTokenSet(){
		if(this.store) await this.authManager.waitUntilReady()
	}

	getRequiredRole(to){
		return getRequiredRole(to)
	}

	redirectToLogin(){
		let name = this.authManager.getLoginPathName()
		// let loginUrl = this.router.resolve({name: name}).href
		console.log("RouterManager logout: name = ", name)//, ", loginUrl = ", loginUrl)
		return {name}
	}

	redirectRouteNameIfNeeded(to){
		let redirectMap = this.options.routeRedirectMapByName || {}
		let redirectName = to?.name ? redirectMap[to.name] : null
		if (!redirectName) {
			return false
		}
		return {
			name: redirectName,
			params: to.params,
			query: to.query,
			replace: true,
		}
	}

	isWhitelistedFn(to){
		return isWhitelisted(to)
	}

	syncPathWithRole(to, role, isQwoachV2){
		if(role === RoleEnum.CLIENT && to.path.startsWith("/app/")){
			return isQwoachV2 ? {name: 'ClientMain'} : {name: 'ClientPortalOldRouteName'}//to.path.replace("/app/", "/cl/")
		}
		if(role === RoleEnum.COACH && to.path.startsWith("/cl/")){
			return {name: 'CoachMain'}//to.path.replace("/cl/", "/app/")
		}
	}

}
